import {useDrag, useDrop} from "react-dnd";
import {ButtonBase, Divider, Stack, Typography} from "@mui/material";
import Straight from "@mui/icons-material/Straight";
import {translate} from "@util";
import {useEffect, useRef, useState} from "react";
import {useThemeContext} from "@theme";
import {getEmptyImage} from "react-dnd-html5-backend";
import {entity} from "@core/model";

const ITEM_TYPE = "FIELD";

type DraggableFieldProps = {
  entity: entity;
  field: string;
  index: number;
  moveField: (dragIndex: number, hoverIndex: number) => void;
  sort?: "ASC" | "DESC";
  onSort: (field: string) => void;
};

const DraggableField = ({
  entity,
  field,
  index,
  moveField,
  sort,
  onSort,
}: DraggableFieldProps) => {
  const {theme} = useThemeContext();
  const [isOverCurrent, setIsOverCurrent] = useState<boolean>(false);
  const [upOrDown, setUpOrdDown] = useState<"up" | "down">();

  const ref = useRef<HTMLDivElement>(null);

  // Drag
  const [{isDragging}, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: {index},
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Drop
  const [{isOver}, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: {index: number}) => {
      if (item.index !== index) {
        moveField(item.index, index);
        item.index = index;
      }
      setIsOverCurrent(false);
    },
    hover: (item: {index: number}) => {
      setIsOverCurrent(item.index === index);
      setUpOrdDown(item.index > index ? "up" : "down");
    },
    collect: monitor => ({
      isOver: monitor.isOver({shallow: true}),
    }),
  });

  drag(drop(ref));

  useEffect(() => {
    preview(getEmptyImage(), {captureDraggingState: true});
  }, []);

  return (
    <Stack sx={{position: "relative"}}>
      {isOver && !isOverCurrent && (
        <Divider
          sx={{
            position: "absolute",
            background: theme.palette.secondary.dark,
            height: "2px",
            left: 0,
            right: 0,
            ml: 1,
            mr: 1,
            ...(upOrDown === "up" ? {top: 0} : {bottom: 0}),
          }}
        />
      )}

      <ButtonBase
        key={"sort_" + field}
        onClick={() => onSort(field)}
        sx={{width: "100%"}}>
        <Stack
          direction="row"
          ref={ref}
          width={"100%"}
          justifyContent={"space-between"}
          p={1}
          gap={1}
          sx={{
            opacity: isDragging ? 0.5 : 1,
            cursor: "grab",
          }}>
          <Typography variant="caption">
            {translate(`entity.${entity}.${field}`)}
          </Typography>
          {sort === "ASC" && <Straight fontSize="small" color="action" />}
          {sort === "DESC" && (
            <Straight
              fontSize="small"
              sx={{transform: "rotate(180deg)"}}
              color="action"
            />
          )}
        </Stack>
      </ButtonBase>
    </Stack>
  );
};

export {DraggableField};
